import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';

export const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  // Send the HttpOnly refresh cookie on auth requests (same-origin in prod;
  // requires the dev origin to be in the backend ALLOWED_ORIGINS whitelist,
  // since CORS AllowCredentials cannot use a "*" origin).
  withCredentials: true,
});

// In-memory access token accessor. authStore registers its getter here at module
// init. This indirection avoids a static import cycle (authStore imports `api`)
// while keeping the token out of localStorage.
let accessTokenGetter: () => string | null = () => null;

export function registerAccessTokenGetter(getter: () => string | null): void {
  accessTokenGetter = getter;
}

// Request interceptor for adding the in-memory access token.
api.interceptors.request.use(
  (config) => {
    const token = accessTokenGetter();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Mutex to prevent parallel refresh requests
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

async function doRefresh(): Promise<string> {
  // No body: the refresh token is carried by the HttpOnly `refresh_token`
  // cookie. withCredentials ensures the cookie is attached. The backend rotates
  // the cookie and returns only a new access token.
  const response = await axios.post(
    `${API_URL}/api/v1/auth/token/refresh`,
    undefined,
    { withCredentials: true }
  );

  const { token } = response.data.data;

  // Update Zustand store (lazy import to avoid circular dependency)
  const { useAuthStore } = await import('../stores/authStore');
  useAuthStore.getState().setToken(token);

  return token;
}

/**
 * bootstrapSession attempts a silent refresh on app load. If the HttpOnly
 * refresh cookie is present and valid, it mints a fresh access token into the
 * in-memory store (session survives a page reload). If there is no cookie / it
 * is expired, the backend returns 401 and we resolve to "not authenticated".
 *
 * Returns true when an access token was obtained, false otherwise. Never throws.
 */
export async function bootstrapSession(): Promise<boolean> {
  try {
    await refreshAccessToken();
    return true;
  } catch {
    return false;
  }
}

// Response interceptor with automatic silent refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/token/refresh')
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        // Refresh failed: drop the in-memory session and bounce to login.
        const { useAuthStore } = await import('../stores/authStore');
        useAuthStore.setState({ token: null, user: null, originalUser: null, isImpersonating: false });
        localStorage.removeItem('isImpersonating');
        localStorage.removeItem('originalUser');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);
